/**
 * Checkout Page
 * Payment and guest registration (auto-register during checkout)
 * Stripe Elements integration for payment processing
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { OrderSummary } from '../../components/order/OrderSummary';
import { useProviderConfig } from '../../contexts/ProviderConfigContext';
import { checkoutService } from '../../services/checkout.service';
import { checkoutSchema, type CheckoutForm } from '../../schemas/checkout.schema';
import { TrustBadges, InclusivePricingCard, OverageRatesCard, MaterialRestrictionsCard } from '../../components/checkout';
import { useRetry } from '../../hooks/useRetry';
import { getErrorMessage } from '../../utils/errorMessages';
import { ErrorAlert } from '../../components/widget/ErrorAlert';
import { usePhoneValidation } from '../../hooks/usePhoneValidation';
import { Button } from '../../components/ui/button';
import { FieldError } from '../../components/widget/FieldError';
import { EnhancedPricingBreakdown, StickyPricingSummary, type PricingLineItem, type ConditionalWarning } from '../../components/pricing/EnhancedPricingBreakdown';
import { LegalAcknowledgments, DEFAULT_PRRO_ACKNOWLEDGMENTS } from '../../components/checkout/LegalAcknowledgments';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

// Load Stripe (use test key for development)
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder'
);

/**
 * Helper function to build PRRO pricing line items from cart data
 */
function buildPRROPricingLineItems(
  cart: { containerSize: string; materialType: string },
  pricing: { basePricing?: Record<string, number>; disposalFees?: Record<string, number>; deliveryFee?: number }
): PricingLineItem[] {
  const items: PricingLineItem[] = [];

  // Container rental
  const rentalFee = pricing.basePricing?.[cart.containerSize] || 0;
  if (rentalFee > 0) {
    items.push({
      id: 'container_rental',
      label: `${cart.containerSize} Container Rental (7 days)`,
      amount: rentalFee,
      type: 'base',
      tooltip: 'Includes 7-day rental period. Extended rental fees may apply after.',
    });
  }

  // Disposal fee (included tonnage)
  const disposalFee = pricing.disposalFees?.[cart.materialType] || 0;
  if (disposalFee > 0) {
    items.push({
      id: 'disposal_included',
      label: 'Disposal (5 tons included)',
      amount: disposalFee,
      included: true,
      type: 'base',
      tooltip: 'Includes disposal of up to 5 tons. Additional tonnage charged separately.',
    });
  }

  // Delivery & pickup
  const deliveryFee = pricing.deliveryFee || 0;
  if (deliveryFee > 0) {
    items.push({
      id: 'delivery',
      label: 'Delivery & Pickup',
      amount: deliveryFee,
      included: true,
      type: 'base',
    });
  }

  return items;
}

/**
 * Helper function to build conditional warnings for PRRO
 */
function buildConditionalWarnings(
  _cart: unknown,
  materialType: string
): ConditionalWarning[] {
  const warnings: ConditionalWarning[] = [];

  // Overweight warning (California 10-ton limit)
  warnings.push({
    id: 'overweight',
    condition: 'If container exceeds 10 tons',
    description: 'California weight limit for roll-off containers',
    estimatedCost: 25000, // $250 in cents
    severity: 'warning',
  });

  // Contamination warning (if mixed C&D)
  if (materialType === 'mixed_cd' || materialType === 'construction_debris') {
    warnings.push({
      id: 'contamination',
      condition: 'If load contains treated wood',
      description: 'Treated wood disposal rate applies to entire load',
      estimatedCost: { min: 30000, max: 70000 }, // $300-$700 range
      severity: 'error',
    });
  }

  return warnings;
}

/**
 * Extract prohibited items for a specific material type
 */
function getProhibitedItemsForMaterial(
  materialType: string,
  prohibitedItems?: Array<{ category: string; items: string[] }>
): string[] {
  if (!prohibitedItems || prohibitedItems.length === 0) {
    return [];
  }

  // Find the category that matches the material type
  const category = prohibitedItems.find(cat =>
    cat.category.toLowerCase().includes(materialType.toLowerCase()) ||
    materialType.toLowerCase().includes(cat.category.toLowerCase())
  );

  return category?.items || [];
}

/**
 * Check if PRRO is enabled for this provider
 * TODO: Replace with actual feature flag from provider config
 */
function isPRROEnabled(_config: unknown): boolean {
  // For now, check if provider has specific PRRO-related config
  // This will be replaced with: config?.features?.prroEnabled ?? false
  return false; // Disabled by default until backend implements feature flag
}

/**
 * Checkout Form Component (wrapped in Stripe Elements)
 */
function CheckoutForm() {
  const navigate = useNavigate();
  const { providerSlug } = useParams<{ providerSlug: string }>();
  const { config } = useProviderConfig();
  const stripe = useStripe();
  const elements = useElements();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<{ message: string; suggestion?: string } | null>(null);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const { retry, isRetrying } = useRetry({ maxRetries: 2 }); // Fewer retries for payment operations
  const { format: formatPhone, inputProps: phoneInputProps } = usePhoneValidation();

  // PRRO-specific state
  const [acknowledgedGroups, setAcknowledgedGroups] = useState<Set<string>>(new Set());
  const [showPricingDetails, setShowPricingDetails] = useState(false);

  // Check if PRRO is enabled
  const prroEnabled = isPRROEnabled(config);

  // Load cart from session storage
  const cart = checkoutService.loadCartFromStorage();

  // Redirect if no cart
  useEffect(() => {
    if (!cart) {
      navigate('../order');
    }
  }, [cart, navigate]);

  // Form setup with watch for retry functionality
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CheckoutForm>({
    resolver: zodResolver(checkoutSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  // Format phone number on change
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setValue('phone', formatted);
  };

  // Handle legal acknowledgment changes
  const handleAcknowledgmentChange = (groupId: string, acknowledged: boolean) => {
    setAcknowledgedGroups(prev => {
      const newSet = new Set(prev);
      if (acknowledged) {
        newSet.add(groupId);
      } else {
        newSet.delete(groupId);
      }
      return newSet;
    });
  };

  // Calculate pricing totals
  const calculateTotal = (
    cart: { containerSize: string; materialType: string },
    pricing: { basePricing?: Record<string, number>; disposalFees?: Record<string, number>; deliveryFee?: number }
  ) => {
    const rentalFee = pricing.basePricing?.[cart.containerSize] || 0;
    const deliveryFee = pricing.deliveryFee || 0;
    const disposalFee = pricing.disposalFees?.[cart.materialType] || 0;
    return rentalFee + deliveryFee + disposalFee;
  };

  // Check if all PRRO acknowledgments are complete
  const allAcknowledged = !prroEnabled || acknowledgedGroups.size === DEFAULT_PRRO_ACKNOWLEDGMENTS.length;

  /**
   * Process checkout (extracted for better error handling)
   */
  const processCheckout = async (data: CheckoutForm) => {
    if (!stripe || !elements || !providerSlug || !cart) {
      throw new Error('Required checkout components not initialized');
    }

    // 1. Get or create checkout session
    const sessionId = await checkoutService.getOrCreateSession(providerSlug);

    // 2. Add cart item to session
    await checkoutService.addItem(providerSlug, sessionId, cart);

    // 3. Set contact info
    await checkoutService.setContactInfo(providerSlug, sessionId, {
      name: data.name,
      email: data.email,
      phone: data.phone,
      companyName: data.companyName,
      createPassword: data.createPassword,
    });

    // 4. Create payment intent
    const paymentIntent = await checkoutService.createPaymentIntent(providerSlug, sessionId);

    // 5. Confirm payment with Stripe
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      throw new Error('Card element not found');
    }

    const { error: stripeError, paymentIntent: confirmedPayment } =
      await stripe.confirmCardPayment(paymentIntent.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: data.name,
            email: data.email,
            phone: data.phone,
          },
        },
      });

    // Handle Stripe-specific errors
    if (stripeError) {
      // Map Stripe error codes to user-friendly messages
      const errorMessage = getErrorMessage(
        { code: stripeError.code || 'PAYMENT_FAILED', message: stripeError.message || 'Payment failed' },
        true // isStripeError
      );
      throw Object.assign(new Error(errorMessage.message), {
        code: stripeError.code,
        suggestion: errorMessage.suggestion
      });
    }

    if (!confirmedPayment || confirmedPayment.status !== 'succeeded') {
      throw new Error('Payment was not successful. Please try again.');
    }

    // 6. Complete checkout on backend
    const orderConfirmation = await checkoutService.completeCheckout(
      providerSlug,
      sessionId,
      confirmedPayment.id,
      {
        name: data.name,
        email: data.email,
        phone: data.phone,
        companyName: data.companyName,
        createPassword: data.createPassword,
      }
    );

    return orderConfirmation;
  };

  /**
   * Handle checkout submission
   */
  const onSubmit = async (data: CheckoutForm) => {
    if (!stripe || !elements || !providerSlug || !cart) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const orderConfirmation = await processCheckout(data);

      // Navigate to confirmation page
      navigate(`../confirmation/${orderConfirmation.orderId}`, {
        state: { orderNumber: orderConfirmation.orderNumber },
      });
    } catch (err) {
      console.error('Checkout error:', err);

      // Extract error message and suggestion
      const errorInfo = getErrorMessage(err, true);
      setError({
        message: errorInfo.message,
        suggestion: errorInfo.suggestion,
      });

      // Focus first error field if validation errors exist
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        document.getElementById(firstErrorField)?.focus();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Retry checkout with same form data
   */
  const handleRetryCheckout = () => {
    const formData = watch();
    retry(() => processCheckout(formData as CheckoutForm))
      .then((orderConfirmation) => {
        // Navigate to confirmation page on success
        navigate(`../confirmation/${orderConfirmation.orderId}`, {
          state: { orderNumber: orderConfirmation.orderNumber },
        });
        setError(null);
      })
      .catch((err) => {
        const errorInfo = getErrorMessage(err, true);
        setError({
          message: errorInfo.message,
          suggestion: errorInfo.suggestion,
        });
      });
  };

  if (!cart) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip Navigation Link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:border-2 focus:border-primary focus:rounded focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-xl font-semibold text-gray-900">
            {config?.companyName || 'Checkout'}
          </h1>
        </div>
      </header>

      {/* Main content */}
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Order summary first on mobile, second on desktop */}
          <div className="lg:col-span-1 lg:order-2">
            <div className="lg:sticky lg:top-8">
              {config?.enhancedPricing && config?.pricing ? (
                /* Enhanced Pricing Display */
                <div className="space-y-4">
                  <InclusivePricingCard
                    containerSize={cart.containerSize}
                    materialType={cart.materialType}
                    basePrice={config.pricing.basePricing?.[cart.containerSize] || 0}
                    includedTonnage={config.enhancedPricing.includedTonnage[cart.containerSize] || 0}
                    rentalDays={config.enhancedPricing.rentalPeriodDays}
                    includesDelivery={true}
                  />

                  <OverageRatesCard
                    overagePerTon={config.enhancedPricing.overageRatePerTon}
                    dailyRentalRate={config.enhancedPricing.dailyExtensionRate}
                    includedTonnage={config.enhancedPricing.includedTonnage[cart.containerSize] || 0}
                    rentalDays={config.enhancedPricing.rentalPeriodDays}
                  />

                  <MaterialRestrictionsCard
                    materialType={cart.materialType}
                    prohibitedItems={getProhibitedItemsForMaterial(cart.materialType, config.prohibitedItems)}
                    contaminationFeeRange={config.enhancedPricing.contaminationFeeRange}
                  />
                </div>
              ) : prroEnabled && config?.pricing ? (
                /* PRRO Enhanced Pricing Breakdown (legacy) */
                <EnhancedPricingBreakdown
                  lineItems={buildPRROPricingLineItems(cart, config.pricing)}
                  conditionalWarnings={buildConditionalWarnings(cart, cart.materialType)}
                  subtotal={calculateTotal(cart, config.pricing)}
                  iwmaFee={{
                    description: 'State-mandated Integrated Waste Management Act fee',
                    appliedAtService: true,
                  }}
                  total={calculateTotal(cart, config.pricing)}
                  estimatedTotal={true}
                  disclaimer="Final pricing based on actual weight and material composition at time of service. You will receive a final invoice after container pickup and waste processing."
                  collapsible={false}
                  defaultExpanded={true}
                />
              ) : (
                /* Standard Order Summary (non-PRRO) */
                <OrderSummary
                  item={cart}
                  pricing={config?.pricing || {
                    basePricing: {},
                    disposalFees: {},
                    rentalPeriodDays: 14,
                  }}
                  showEdit={true}
                  onEdit={() => navigate('../order')}
                />
              )}
            </div>
          </div>

          {/* Form second on mobile, first on desktop */}
          <div className="lg:col-span-2 lg:order-1">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Error message */}
              {error && (
                <ErrorAlert
                  error={error}
                  onRetry={handleRetryCheckout}
                  isRetrying={isRetrying}
                  dismissible
                  onDismiss={() => setError(null)}
                />
              )}

              {/* Contact information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>

                <div className="space-y-5">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500" aria-label="required">*</span>
                    </label>
                    <input
                      {...register('name')}
                      type="text"
                      id="name"
                      placeholder="John Doe"
                      autoComplete="name"
                      className={`w-full min-h-[48px] px-4 py-3 text-base border rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                        errors.name
                          ? 'border-red-500 bg-red-50 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-primary focus:border-primary'
                      }`}
                      aria-required="true"
                      aria-invalid={errors.name ? "true" : "false"}
                      aria-describedby={errors.name ? "name-error" : undefined}
                    />
                    <FieldError id="name-error" message={errors.name?.message} />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500" aria-label="required">*</span>
                    </label>
                    <input
                      {...register('email')}
                      type="email"
                      id="email"
                      placeholder="john@example.com"
                      autoComplete="email"
                      className={`w-full min-h-[48px] px-4 py-3 text-base border rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                        errors.email
                          ? 'border-red-500 bg-red-50 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-primary focus:border-primary'
                      }`}
                      aria-required="true"
                      aria-invalid={errors.email ? "true" : "false"}
                      aria-describedby={errors.email ? "email-error" : undefined}
                    />
                    <FieldError id="email-error" message={errors.email?.message} />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone <span className="text-red-500" aria-label="required">*</span>
                    </label>
                    <input
                      {...register('phone', {
                        onChange: handlePhoneChange
                      })}
                      {...phoneInputProps}
                      inputMode="tel"
                      id="phone"
                      placeholder="(555) 123-4567"
                      className={`w-full min-h-[48px] px-4 py-3 text-base border rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                        errors.phone
                          ? 'border-red-500 bg-red-50 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-primary focus:border-primary'
                      }`}
                      aria-required="true"
                      aria-invalid={errors.phone ? "true" : "false"}
                      aria-describedby={errors.phone ? "phone-error" : undefined}
                    />
                    <FieldError id="phone-error" message={errors.phone?.message} />
                  </div>

                  {/* Company (optional) */}
                  <div>
                    <label
                      htmlFor="companyName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Company <span className="text-gray-500">(Optional)</span>
                    </label>
                    <input
                      {...register('companyName')}
                      type="text"
                      id="companyName"
                      placeholder="ABC Construction"
                      autoComplete="organization"
                      className="w-full min-h-[48px] px-4 py-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>

                  {/* Create password checkbox */}
                  <div className="flex items-start">
                    <div className="inline-flex items-center justify-center min-h-[44px] min-w-[44px]">
                      <input
                        type="checkbox"
                        id="showPassword"
                        checked={showPasswordField}
                        onChange={(e) => setShowPasswordField(e.target.checked)}
                        className="w-5 h-5 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 border-gray-300 rounded cursor-pointer"
                      />
                    </div>
                    <label htmlFor="showPassword" className="flex items-center min-h-[44px] ml-3 text-sm text-gray-700 cursor-pointer">
                      Create an account to track orders
                    </label>
                  </div>

                  {/* Password field (conditional) */}
                  {showPasswordField && (
                    <div>
                      <label
                        htmlFor="createPassword"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Password <span className="text-red-500" aria-label="required">*</span>
                      </label>
                      <input
                        {...register('createPassword')}
                        type="password"
                        id="createPassword"
                        placeholder="Min 8 characters"
                        autoComplete="new-password"
                        className={`w-full min-h-[48px] px-4 py-3 text-base border rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                          errors.createPassword
                            ? 'border-red-500 bg-red-50 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:ring-primary focus:border-primary'
                        }`}
                        aria-required="true"
                        aria-invalid={errors.createPassword ? "true" : "false"}
                        aria-describedby={errors.createPassword ? "createPassword-error" : undefined}
                      />
                      <FieldError id="createPassword-error" message={errors.createPassword?.message} />
                    </div>
                  )}
                </div>
              </div>

              {/* Legal Acknowledgments (PRRO only) */}
              {prroEnabled && (
                <LegalAcknowledgments
                  groups={DEFAULT_PRRO_ACKNOWLEDGMENTS}
                  termsUrl={config?.website ? `${config.website}/terms` : undefined}
                  onAcknowledgmentChange={handleAcknowledgmentChange}
                  acknowledgedGroups={acknowledgedGroups}
                />
              )}

              {/* Payment information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h2>

                {/* Trust Badges - moved before payment form */}
                <TrustBadges />

                {/* Stripe Card Element */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Details <span className="text-red-500">*</span>
                  </label>
                  <div className="border border-gray-300 rounded-md p-3 bg-white min-h-[44px]">
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            lineHeight: '24px',
                            color: '#424770',
                            '::placeholder': {
                              color: '#aab7c4',
                            },
                          },
                          invalid: {
                            color: '#9e2146',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Submit button */}
              <Button
                type="submit"
                disabled={!stripe || isSubmitting || !allAcknowledged}
                size="lg"
                className="w-full"
                aria-label={
                  !allAcknowledged
                    ? 'Please acknowledge all terms before placing order'
                    : isSubmitting
                    ? 'Processing payment'
                    : 'Place order'
                }
              >
                {isSubmitting ? 'Processing Payment...' : 'Place Order'}
              </Button>

              {/* Accessibility helper for disabled state */}
              {!allAcknowledged && prroEnabled && (
                <p className="text-sm text-amber-600 mt-2 text-center" role="status" aria-live="polite">
                  Please acknowledge all terms above to continue
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Mobile Sticky Pricing Summary (PRRO only) */}
        {prroEnabled && cart && config?.pricing && (
          <>
            <StickyPricingSummary
              total={calculateTotal(cart, config.pricing)}
              estimatedTotal={true}
              onViewDetails={() => setShowPricingDetails(true)}
              className="md:hidden"
            />

            {/* Pricing Details Modal for Mobile */}
            <Dialog open={showPricingDetails} onOpenChange={setShowPricingDetails}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Pricing Details</DialogTitle>
                </DialogHeader>
                <EnhancedPricingBreakdown
                  lineItems={buildPRROPricingLineItems(cart, config.pricing)}
                  conditionalWarnings={buildConditionalWarnings(cart, cart.materialType)}
                  subtotal={calculateTotal(cart, config.pricing)}
                  iwmaFee={{
                    description: 'State-mandated Integrated Waste Management Act fee',
                    appliedAtService: true,
                  }}
                  total={calculateTotal(cart, config.pricing)}
                  estimatedTotal={true}
                  disclaimer="Final pricing based on actual weight and material composition at time of service."
                  collapsible={false}
                  defaultExpanded={true}
                />
              </DialogContent>
            </Dialog>
          </>
        )}
      </main>

      {/* Add bottom padding on mobile to account for sticky summary */}
      {prroEnabled && <div className="h-24 md:hidden" aria-hidden="true" />}
    </div>
  );
}

/**
 * Checkout Page (wrapped with Stripe Elements provider)
 */
export function Checkout() {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm />
    </Elements>
  );
}
